/**
 * Dual-currency display helpers for the deposit web app.
 *
 * EUR is the settlement currency. For a non-EUR dealer, the UI appends
 * an approximate local-currency figure next to the EUR value, for example,
 * "329.00 (≈ 303.80 CHF)". The number format matches the app's existing
 * `toFixed(2)` style so EUR-only output stays byte-identical.
 *
 * The ≈ sign marks a live estimate. Legal invoices render the frozen snapshot
 * value without that marker.
 */
export const APP_CURRENCY = "EUR";
const APPROX = "≈";

export interface SecondaryRate {
  currency: string;
  rate: number;
}

/** Formats EUR using two decimal places and a decimal point. */
export function formatEurNumber(eur: number): string {
  return Number(eur).toFixed(2);
}

/** Formats a converted value with its ISO currency code. */
export function formatSecondaryNumber(
  eur: number,
  rate: SecondaryRate,
): string {
  return `${(Number(eur) * rate.rate).toFixed(2)} ${rate.currency}`;
}

/**
 * Formats the amount body without a leading currency symbol.
 */
export function dualAmount(eur: number, rate: SecondaryRate | null): string {
  const base = formatEurNumber(eur);
  if (
    !rate ||
    !rate.currency ||
    rate.currency === APP_CURRENCY ||
    !(rate.rate > 0)
  ) {
    return base;
  }
  return `${base} (${APPROX} ${formatSecondaryNumber(eur, rate)})`;
}
